import { times } from 'lodash';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import Grid, { Row, Col } from 'hb-react/shared/components/DesignSystem/Grid';

import TopicItem from './TopicItem';
import { CATEGORY } from '../constants/types';
import { useTopicsIndexContext } from '../contexts/TopicsIndexContext';

const TopicsAsGrid = ({
  loading,
  topics,
  total,
  loadNextPage,
  updateTopicApproval,
  LoadingItemComponent,
}) => {
  const { currentView } = useTopicsIndexContext();
  const { currentNetwork } = useDefaultAppContexts();
  const configDescriptionIsNotVisible = !Number(
    currentNetwork?.features?.topics?.config
      ?.description_visible_in_frontoffice,
  );

  // we can only hide the empty description filler block in topic card
  // if LD flag is enabled and all topics in are missing their description
  const canRemoveDescriptionPlaceholder = useMemo(() => {
    if (topics.length) {
      return !topics.some(({ description }) => description);
    }

    return configDescriptionIsNotVisible;
  }, [topics]);

  return (
    <Grid
      width="100%"
      minWidth={0}
      data-testid="topics-as-grid"
      height="fit-content"
    >
      {topics.length === 0 && loading ? (
        <Row>
          {times(4, (i) => (
            <Col
              key={i}
              width={{ m: 1 / 2, l: 1 / 3, xl: 1 / 4 }}
              pt={currentView === CATEGORY ? 6 : 0}
              css={css`
                width: 100%;
              `}
            >
              <LoadingItemComponent
                noDescriptionPlaceholder={canRemoveDescriptionPlaceholder}
              />
            </Col>
          ))}
        </Row>
      ) : (
        <>
          <Row>
            {topics.map((topic) => (
              <Col
                key={topic.id}
                width={{ m: 1 / 2, l: 1 / 3, xl: 1 / 4 }}
                pt={currentView === CATEGORY ? 6 : 0}
                css={css`
                  width: 100%;
                `}
              >
                <TopicItem
                  canRemoveDescriptionPlaceholder={
                    canRemoveDescriptionPlaceholder
                  }
                  currentNetwork={currentNetwork}
                  topic={topic}
                  updateTopicApproval={updateTopicApproval}
                />
              </Col>
            ))}
          </Row>
          {total && (
            <Row>
              <Col width={12 / 12} pt={2}>
                <Box flex={1} justifyContent="center">
                  {topics.length < total && (
                    <Button onClick={loadNextPage} isLoading={loading}>
                      {__('Load More')}
                    </Button>
                  )}
                </Box>
              </Col>
            </Row>
          )}
        </>
      )}
    </Grid>
  );
};

TopicsAsGrid.protoTypes = {
  loading: PropTypes.bool.isRequired,
  topics: PropTypes.array.isRequired,
  total: PropTypes.number.isRequired,
  cardsPerRow: PropTypes.object.isRequired,
  loadNextPage: PropTypes.func.isRequired,
  updateTopicApproval: PropTypes.func,
  LoadingItemComponent: PropTypes.elementType.isRequired,
};

export default TopicsAsGrid;
